import type { MatchMode } from "../game/types";
import type { ProgressSnapshot } from "../progress/session";
import { Header, type NavigationProps } from "./SiteHeader";
import { CosmeticPreview } from "./CosmeticPreview";
import { cosmeticCopy } from "./cosmetics";
import { Icon } from "./Icon";

export function Home({
  progress,
  selectedMode,
  onModeChange,
  onStart,
  onNavigate,
}: NavigationProps & {
  readonly progress: ProgressSnapshot;
  readonly selectedMode: MatchMode;
  readonly onModeChange: (mode: MatchMode) => void;
  readonly onStart: () => void;
}) {
  const { records, equippedCosmetic, settings } = progress.progress;
  const endless = selectedMode === "endless";
  const skin = cosmeticCopy[equippedCosmetic];
  const stats = endless
    ? [
        {
          label: "最长航程",
          value: `${Math.floor(records.longestEndlessSessionMs / 60000)}:${String(Math.floor(records.longestEndlessSessionMs / 1000) % 60).padStart(2, "0")}`,
          icon: "clock" as const,
        },
        {
          label: "最长线迹",
          value: records.longestEndlessLength,
          icon: "spark" as const,
        },
        {
          label: "无尽航程",
          value: records.endlessSessions,
          icon: "infinity" as const,
        },
      ]
    : [
        {
          label: "最佳信号",
          value: records.bestScore,
          icon: "trophy" as const,
        },
        {
          label: "最长线迹",
          value: records.longestLength,
          icon: "spark" as const,
        },
        {
          label: "完成对局",
          value: records.matchesPlayed,
          icon: "play" as const,
        },
      ];
  return (
    <>
      <Header onNavigate={onNavigate} progress={progress} />
      <main className="home">
        <div className="lobby-heading">
          <p className="eyebrow">
            <span className="tiny-orbit" />
            一场属于你的微光漫游
          </p>
          <span className="edition-label">THE AFTERGLOW COLLECTION · 01</span>
        </div>
        <section className="lobby" aria-label="开始你的航程">
          <div className="lobby-art">
            <div className="lobby-title">
              <p className="eyebrow">FOLLOW YOUR OWN GLOW</p>
              <h1 aria-label="Gloamline 暮光线迹">
                暮光<span>线迹</span>
                <i>Gloamline</i>
              </h1>
              <p>
                拾起微光，蜿蜒生长。
                <br />
                在暮色里，画出你的下一步。
              </p>
            </div>
            <div className="showcase-orbit orbit-one" aria-hidden="true" />
            <div className="showcase-orbit orbit-two" aria-hidden="true" />
            <div className="hero-specimen">
              <CosmeticPreview
                cosmetic={equippedCosmetic}
                theme={settings.theme}
                variant="hero"
              />
            </div>
            <span className="specimen-coordinate" aria-hidden="true">
              SPECIMEN {skin.subtitle.split(" / ")[1]}
              <br />
              IN THE QUIET, WE GLOW.
            </span>
            <div className="equipped-strip">
              <span className="skin-swatch" data-cosmetic={equippedCosmetic} />
              <div>
                <small>你的线迹</small>
                <strong>{skin.name}</strong>
              </div>
              <button
                className="text-button"
                onClick={() => onNavigate("appearance")}
                aria-label="外观与解锁"
              >
                更换外观
                <Icon name="arrow" />
              </button>
            </div>
          </div>
          <div className="play-panel">
            <div className="play-panel-heading">
              <span className="section-number">01 / PLAY</span>
              <span className="quiet-badge">
                <i className="status-dot" />
                无需登录
              </span>
            </div>
            <h2>准备出发。</h2>
            <p className="panel-intro">选择你的节奏，下一道光由你点亮。</p>
            <fieldset className="mode-selector">
              <legend>选择航程</legend>
              <label data-selected={!endless}>
                <input
                  type="radio"
                  name="match-mode"
                  value="signal"
                  checked={!endless}
                  onChange={() => onModeChange("signal")}
                />
                <span className="mode-icon">
                  <Icon name="trophy" />
                </span>
                <span className="mode-copy">
                  <strong>逐光竞技</strong>
                  <small>Signal Drift · 180 秒 · 单生命</small>
                </span>
                <span className="radio-indicator" aria-hidden="true" />
              </label>
              <label data-selected={endless}>
                <input
                  type="radio"
                  name="match-mode"
                  value="endless"
                  checked={endless}
                  onChange={() => onModeChange("endless")}
                />
                <span className="mode-icon">
                  <Icon name="infinity" />
                </span>
                <span className="mode-copy">
                  <strong>无尽漫游</strong>
                  <small>Endless Drift · 无时限 · 可复活</small>
                </span>
                <span className="radio-indicator" aria-hidden="true" />
              </label>
            </fieldset>
            <p className="mode-note" aria-live="polite">
              {endless
                ? "慢慢探索。熄灭后 5 秒重新加入，带着短暂防护继续生长。"
                : "与 9 条机器人同场逐光，吃下微光成长，用走位争取更高排名。"}
            </p>
            <button
              className="primary-button start-button"
              disabled={progress.authTransitioning}
              onClick={onStart}
            >
              <Icon name="play" />
              {progress.authTransitioning ? "正在切换身份…" : "立即开始"}
              <Icon name="arrow" />
            </button>
            <button
              className="text-button how-to-button"
              onClick={() => onNavigate("help")}
            >
              <Icon name="help" />
              操作与规则
            </button>
            <div className="control-legend">
              <span>
                <kbd>W A S D</kbd>
                <kbd>↑ ↓ ← →</kbd> / 指针转向
              </span>
              <span>
                <kbd>Space</kbd> / <kbd>Shift</kbd> 加速
              </span>
            </div>
          </div>
        </section>
        <section
          className="lobby-bottom"
          aria-label={endless ? "无尽纪录" : "竞技纪录"}
        >
          <div className="record-strip">
            {stats.map((stat) => (
              <div className="record-item" key={stat.label}>
                <Icon name={stat.icon} />
                <div>
                  <span className="record-label">{stat.label}</span>
                  <strong className="record-value">{stat.value}</strong>
                </div>
              </div>
            ))}
          </div>
          <div className="lobby-note">
            <Icon name="shield" />
            <p>
              自由生长，公平相遇。
              <span>所有外观只改变模样，每一条线迹遵守相同规则。</span>
            </p>
          </div>
        </section>
        <footer className="site-footer">
          <span>原创浏览器游戏 · 为片刻的心流而作</span>
          <span>
            键鼠与触屏皆可游玩
            <Icon name="spark" />
          </span>
        </footer>
      </main>
    </>
  );
}
